import { EMAIL_REGEX } from '@/constants/static';
import { StepForms } from '@/stores/slices/entity-slice';
import { isValidHttpUrl } from '@/utils/http-url';

import { getRequiredUploadSections, titleToTypeMap } from './common';

export const validateStep = (
  currentStep: number,
  stepForms: StepForms,
  regionSelectOptions?: { options: { label: string; value: string }[]; required: boolean },
) => {
  if (currentStep === 1) {
    const hasRegions =
      !!stepForms.country &&
      (regionSelectOptions?.options?.length ?? 0) > 0 &&
      regionSelectOptions?.required;

    const requiredFieldsFilled =
      stepForms.legalName?.trim() &&
      stepForms.displayName?.trim() &&
      stepForms.identificationNumber?.trim() &&
      stepForms.country?.trim() &&
      stepForms.registeredAt &&
      stepForms.type?.trim() &&
      stepForms.entityEmail?.trim() &&
      EMAIL_REGEX.test(stepForms.entityEmail.trim()) &&
      isValidHttpUrl(stepForms.entityWebsite ?? '') &&
      stepForms.registeredAddress?.trim() &&
      stepForms.registeredAddressCity?.trim() &&
      stepForms.registeredAddressPostalCode?.trim() &&
      (!hasRegions || stepForms.state?.trim());

    const isLegalValid =
      stepForms.isLegalNameCorrect === true ||
      stepForms.legalName !== stepForms.lastValidatedLegalName;

    const isDisplayValid =
      stepForms.displayName === null ||
      stepForms.isDisplayNameCorrect === true ||
      stepForms.displayName !== stepForms.lastValidatedDisplayName;

    return Boolean(requiredFieldsFilled && isLegalValid && isDisplayValid);
  }

  if (currentStep === 2) {
    return [...(stepForms.members ?? []), ...(stepForms.invitations ?? [])].some(
      (el) => el.legalRole === 'Director',
    );
  }

  if (currentStep === 3) {
    const validNumberOfShares = stepForms?.shareStructure?.shareClasses?.reduce(
      (acc, cls) =>
        acc +
        (cls.shareholders?.reduce(
          (acc, shareholder) => acc + (shareholder.numberOfShares ?? 0),
          0,
        ) ?? 0),
      0,
    );
    return (
      stepForms?.shareStructure?.shareClasses?.every((shareClass) => {
        const shareholders = shareClass.shareholders;

        const classSharesOk =
          typeof shareClass.numberOfShares === 'number' &&
          Number.isFinite(shareClass.numberOfShares) &&
          shareClass.numberOfShares > 0;
        return (
          !!shareClass.class?.trim() &&
          classSharesOk &&
          Array.isArray(shareholders) &&
          shareholders.length > 0 &&
          shareholders.every(
            (holder) =>
              holder.fullName?.trim() &&
              holder.email?.trim() &&
              EMAIL_REGEX.test(holder.email.trim()) &&
              holder.numberOfShares != null &&
              holder.numberOfShares > 0,
          )
        );
      }) && validNumberOfShares <= (stepForms.shareStructure.totalNumberOfShares ?? 0)
    );
  }

  if (currentStep === 4) {
    return getRequiredUploadSections(stepForms.registeredAt).every((section) => {
      const expectedType = titleToTypeMap[section.title];
      if (!expectedType) return false;
      return stepForms.verifications?.some(
        (v) => v.type === expectedType && (v.format === 'file' || v.format === 'url'),
      );
    });
  }

  return true;
};

// A draft may leave these empty, but the API rejects a filled-in value in the wrong format.
export const hasInvalidContactFormat = ({ entityEmail, entityWebsite }: StepForms) => {
  const email = entityEmail?.trim();

  return Boolean(email && !EMAIL_REGEX.test(email)) || !isValidHttpUrl(entityWebsite ?? '');
};

export const getStepFieldErrors = (
  currentStep: number,
  stepForms: StepForms,
  regionSelectOptions?: { options: { label: string; value: string }[]; required: boolean },
) => {
  const errors: Record<string, boolean> = {};

  if (currentStep === 1) {
    const hasRegions =
      !!stepForms.country &&
      (regionSelectOptions?.options.length ?? 0) > 0 &&
      regionSelectOptions?.required;

    errors.legalName = !stepForms.legalName?.trim();
    errors.displayName = !stepForms.displayName?.trim();
    errors.country = !stepForms.country?.trim();
    errors.identificationNumber = !stepForms.identificationNumber?.trim();
    errors.registeredAt = !stepForms.registeredAt;
    errors.type = !stepForms.type?.trim();
    errors.entityEmail =
      !stepForms.entityEmail?.trim() || !EMAIL_REGEX.test(stepForms.entityEmail.trim());
    errors.entityWebsite = !isValidHttpUrl(stepForms.entityWebsite ?? '');
    errors.registeredAddress = !stepForms.registeredAddress?.trim();
    errors.registeredAddressCity = !stepForms.registeredAddressCity?.trim();
    errors.registeredAddressPostalCode = !stepForms.registeredAddressPostalCode?.trim();
    if (hasRegions) {
      errors.state = !stepForms.state?.trim();
    }
  }

  if (currentStep === 2) {
    const hasDirector = [...(stepForms.members ?? []), ...(stepForms.invitations ?? [])].some(
      (el) => el.legalRole === 'Director',
    );

    if (!hasDirector) {
      errors['members.director'] = true;
    }
  }

  if (currentStep === 3) {
    errors.totalNumberOfShares =
      !stepForms.shareStructure.totalNumberOfShares ||
      stepForms.shareStructure?.shareClasses?.length === 0 ||
      stepForms.shareStructure.shareClasses.reduce(
        (acc, cls) => acc + (cls.numberOfShares ?? 0),
        0,
      ) > stepForms.shareStructure.totalNumberOfShares;
    const shareClasses = stepForms.shareStructure.shareClasses ?? [];

    shareClasses.forEach((cls, classIdx) => {
      if (!cls.class?.trim()) {
        errors[`shareClass.${classIdx}.class`] = true;
      }

      const classSharesOk =
        typeof cls.numberOfShares === 'number' &&
        Number.isFinite(cls.numberOfShares) &&
        cls.numberOfShares > 0;

      if (!classSharesOk) {
        errors[`shareClass.${classIdx}.numberOfShares`] = true;
      }

      const shareholders = cls.shareholders ?? [];
      if (shareholders.length === 0) {
        errors[`shareClass.${classIdx}.shareholders`] = true;
      }

      shareholders.forEach((sh, shareholderIdx) => {
        const base = `shareholders.${classIdx}.${shareholderIdx}`;
        if (!sh.type?.trim()) {
          errors[`${base}.type`] = true;
        }
        if (!sh.fullName?.trim()) {
          errors[`${base}.fullName`] = true;
        }
        if (!sh.email?.trim() || !EMAIL_REGEX.test(sh.email.trim())) {
          errors[`${base}.email`] = true;
        }
        if (sh.numberOfShares == null || sh.numberOfShares <= 0) {
          errors[`${base}.numberOfShares`] = true;
        }
      });
    });
  }

  if (currentStep === 4) {
    getRequiredUploadSections(stepForms.registeredAt).forEach(({ title }) => {
      const expectedType = titleToTypeMap[title];
      const hasFileOrLink = stepForms.verifications?.some(
        (v) => v.type === expectedType && (v.format === 'file' || v.format === 'url'),
      );
      if (!hasFileOrLink) {
        errors[`verifications.${expectedType}`] = true;
      }
    });
  }
  return errors;
};
